import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  PlaybackRequest,
  PlayResult,
  PlayTrackOptions,
  playOnDevice,
} from '../utils/player';

type PlayerStatus = 'idle' | 'loading' | 'ready' | 'not_ready' | 'error';

interface SpotifyPlaybackContextType {
  deviceId: string | null;
  error: string | null;
  playbackState: SpotifyWebPlaybackState | null;
  queue: SpotifyWebPlaybackTrack[];
  queueContext: 'album' | 'queue';
  status: PlayerStatus;
  playAlbum: (albumId: string) => Promise<PlayResult>;
  playArtist: (artistId: string) => Promise<PlayResult>;
  playTrack: (trackId: string) => Promise<PlayResult>;
  playTrackInContext: (options: PlayTrackOptions) => Promise<PlayResult>;
  togglePlay: () => Promise<void>;
  nextTrack: () => Promise<void>;
  previousTrack: () => Promise<void>;
  seek: (positionMs: number) => Promise<void>;
  refreshQueue: () => Promise<void>;
  reconnect: () => Promise<void>;
}

const SpotifyPlaybackContext = createContext<SpotifyPlaybackContextType | null>(null);

const SDK_URL = 'https://sdk.scdn.co/spotify-player.js';
const DEVICE_WAIT_MS = 8000;

const toUri = (type: 'album' | 'artist' | 'track', value: string) =>
  value.startsWith('spotify:') ? value : `spotify:${type}:${value}`;

const getNativeBridge = () =>
  typeof window === 'undefined' ? undefined : window.MusicManAndroid;

const postNativeCommand = (command: Record<string, unknown>) => {
  getNativeBridge()?.postMessage(JSON.stringify(command));
};

export const SpotifyPlaybackProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { accessToken, isAuthenticated } = useAuth();
  const accessTokenRef = useRef<string | null>(accessToken);
  const playerRef = useRef<SpotifyWebPlaybackPlayer | null>(null);
  const playbackStateRef = useRef<SpotifyWebPlaybackState | null>(null);
  const playbackClockRef = useRef({ position: 0, updatedAt: Date.now() });
  const deviceIdRef = useRef<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [playbackState, setPlaybackState] = useState<SpotifyWebPlaybackState | null>(null);
  const [queue, setQueue] = useState<SpotifyWebPlaybackTrack[]>([]);
  const [queueContext, setQueueContext] = useState<'album' | 'queue'>('queue');
  const [status, setStatus] = useState<PlayerStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const initializePlayer = useCallback(() => {
    if (!window.Spotify || playerRef.current || !accessTokenRef.current) return;

    setStatus('loading');
    setError(null);

    const player = new window.Spotify.Player({
      name: 'Music Man',
      getOAuthToken: (callback) => {
        const token = accessTokenRef.current;
        if (token) callback(token);
      },
      volume: 0.8,
      enableMediaSession: true,
    });

    playerRef.current = player;

    player.addListener('ready', ({ device_id }) => {
      deviceIdRef.current = device_id;
      setDeviceId(device_id);
      setStatus('ready');
      setError(null);
    });

    player.addListener('not_ready', ({ device_id }) => {
      if (deviceIdRef.current === device_id) {
        deviceIdRef.current = null;
        setDeviceId(null);
      }
      setStatus('not_ready');
    });

    player.addListener('player_state_changed', (state) => {
      playbackStateRef.current = state;
      playbackClockRef.current = {
        position: state?.position || 0,
        updatedAt: Date.now(),
      };
      setPlaybackState(state);
    });

    const handleSdkError = ({ message }: { message: string }) => {
      setStatus('error');
      setError(message);
    };

    player.addListener('initialization_error', handleSdkError);
    player.addListener('authentication_error', handleSdkError);
    player.addListener('account_error', handleSdkError);
    player.addListener('playback_error', handleSdkError);

    player.connect().then((connected) => {
      if (!connected) {
        setStatus('error');
        setError('Spotify could not initialize in-app playback.');
      }
    }).catch((connectionError: Error) => {
      setStatus('error');
      setError(connectionError.message || 'Spotify could not initialize in-app playback.');
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      playerRef.current?.disconnect();
      playerRef.current = null;
      deviceIdRef.current = null;
      setDeviceId(null);
      setPlaybackState(null);
      playbackStateRef.current = null;
      setQueue([]);
      setQueueContext('queue');
      setStatus('idle');
      return;
    }

    if (getNativeBridge()) {
      setStatus('loading');
      const handleState = (event: Event) => {
        const state = (event as CustomEvent<SpotifyWebPlaybackState>).detail;
        playbackStateRef.current = state;
        playbackClockRef.current = { position: state.position, updatedAt: Date.now() };
        setPlaybackState(state);
      };
      const handleStatus = (event: Event) => {
        const detail = (event as CustomEvent<{ status: PlayerStatus; message?: string }>).detail;
        setStatus(detail.status);
        setError(detail.message || null);
        if (detail.status === 'ready') {
          deviceIdRef.current = 'native-spotify-app';
          setDeviceId('native-spotify-app');
        }
      };
      window.addEventListener('musicman:native-player-state', handleState);
      window.addEventListener('musicman:native-player-status', handleStatus);
      postNativeCommand({ action: 'connect' });
      return () => {
        window.removeEventListener('musicman:native-player-state', handleState);
        window.removeEventListener('musicman:native-player-status', handleStatus);
      };
    }

    if (window.Spotify) {
      initializePlayer();
    } else {
      window.onSpotifyWebPlaybackSDKReady = initializePlayer;
      if (!document.querySelector(`script[src="${SDK_URL}"]`)) {
        const script = document.createElement('script');
        script.src = SDK_URL;
        script.async = true;
        script.onerror = () => {
          setStatus('error');
          setError('The Spotify player could not be loaded. Check your connection and try again.');
        };
        document.body.appendChild(script);
      }
      setStatus('loading');
    }

    return () => {
      window.onSpotifyWebPlaybackSDKReady = undefined;
    };
  }, [isAuthenticated, initializePlayer]);

  const refreshQueue = useCallback(async () => {
    const token = accessTokenRef.current;
    if (!token) return;
    try {
      const response = await fetch('/api/spotify/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: token,
          context_uri: playbackStateRef.current?.context.uri,
        }),
      });
      if (!response.ok) return;
      const data = await response.json() as { queue?: SpotifyWebPlaybackTrack[]; queue_context?: 'album' | 'queue' };
      setQueue(data.queue || []);
      setQueueContext(data.queue_context === 'album' ? 'album' : 'queue');
    } catch (queueError) {
      console.error('Unable to refresh Spotify queue:', queueError);
    }
  }, []);

  const currentTrackId = playbackState?.track_window.current_track.id;
  useEffect(() => {
    if (!currentTrackId) return;
    refreshQueue();
    const interval = window.setInterval(refreshQueue, 30000);
    return () => window.clearInterval(interval);
  }, [currentTrackId, refreshQueue]);

  const waitForDevice = useCallback(async (): Promise<string | null> => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < DEVICE_WAIT_MS) {
      if (deviceIdRef.current) return deviceIdRef.current;
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return null;
  }, []);

  const playRequest = useCallback(async (request: PlaybackRequest): Promise<PlayResult> => {
    if (getNativeBridge()) {
      const uri = request.context_uri || request.uris?.[0];
      if (!uri) return { success: false, message: 'No Spotify track, album, or artist was selected.' };

      let contextIndex: number | undefined;
      if (request.context_uri?.startsWith('spotify:album:') && request.offset?.uri) {
        try {
          const response = await fetch('/api/spotify/album-track-index', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              access_token: accessTokenRef.current,
              album_uri: request.context_uri,
              track_uri: request.offset.uri,
            }),
          });
          const data = await response.json() as { index?: number; error?: string };
          if (!response.ok || data.index === undefined) {
            return { success: false, message: data.error || 'Spotify could not find that track in the album.' };
          }
          contextIndex = data.index;
        } catch {
          return { success: false, message: 'Spotify could not load the album before resuming it.' };
        }
      }

      postNativeCommand({ action: 'play', uri, contextIndex, positionMs: request.position_ms });
      return { success: true };
    }

    const player = playerRef.current;
    const token = accessTokenRef.current;

    if (!player || !token) {
      return { success: false, message: 'The in-app Spotify player is still loading. Try again in a moment.' };
    }

    // Invoke before the first await so Android sees the originating Play tap.
    const activation = player.activateElement();

    try {
      await activation;
    } catch {
      return { success: false, message: 'Tap Play again to enable audio on this phone.' };
    }

    const targetDeviceId = deviceIdRef.current || await waitForDevice();
    if (!targetDeviceId) {
      return { success: false, message: 'Spotify could not make Music Man available as a playback device.' };
    }

    const result = await playOnDevice(request, token, targetDeviceId);
    if (!result.success) setError(result.message || 'Spotify could not start playback.');
    return result;
  }, [waitForDevice]);

  const playAlbum = useCallback(
    (albumId: string) => playRequest({ context_uri: toUri('album', albumId) }),
    [playRequest]
  );

  const playArtist = useCallback(
    (artistId: string) => playRequest({ context_uri: toUri('artist', artistId) }),
    [playRequest]
  );

  const playTrack = useCallback(
    (trackId: string) => playRequest({ uris: [toUri('track', trackId)] }),
    [playRequest]
  );

  const playTrackInContext = useCallback((options: PlayTrackOptions) => {
    const request: PlaybackRequest = {};
    if (options.trackUri && options.albumUri) {
      request.context_uri = options.albumUri;
      request.offset = { uri: options.trackUri };
    } else if (options.albumUri) {
      request.context_uri = options.albumUri;
    } else if (options.trackUri) {
      request.uris = [options.trackUri];
    }
    if (options.positionMs !== undefined) request.position_ms = options.positionMs;
    return playRequest(request);
  }, [playRequest]);

  const togglePlay = useCallback(async () => {
    if (getNativeBridge()) {
      postNativeCommand({ action: 'toggle' });
      return;
    }
    await playerRef.current?.togglePlay();
  }, []);

  const nextTrack = useCallback(async () => {
    if (getNativeBridge()) {
      postNativeCommand({ action: 'next' });
      return;
    }
    await playerRef.current?.nextTrack();
  }, []);

  const previousTrack = useCallback(async () => {
    if (getNativeBridge()) {
      postNativeCommand({ action: 'previous' });
      return;
    }
    await playerRef.current?.previousTrack();
  }, []);

  const seek = useCallback(async (positionMs: number) => {
    if (getNativeBridge()) {
      postNativeCommand({ action: 'seek', positionMs });
      return;
    }
    const player = playerRef.current;
    const currentState = playbackStateRef.current;
    if (!player || !currentState) return;

    const nextPosition = Math.max(0, Math.min(positionMs, currentState.duration));
    try {
      await player.seek(nextPosition);
      const nextState = { ...currentState, position: nextPosition };
      playbackStateRef.current = nextState;
      playbackClockRef.current = { position: nextPosition, updatedAt: Date.now() };
      setPlaybackState(nextState);
    } catch (seekError) {
      setError(seekError instanceof Error ? seekError.message : 'Spotify could not seek to that position.');
    }
  }, []);

  useEffect(() => {
    if (!playbackState || playbackState.paused) return;

    playbackClockRef.current = {
      position: playbackState.position,
      updatedAt: Date.now(),
    };
    const interval = window.setInterval(() => {
      const currentState = playbackStateRef.current;
      if (!currentState || currentState.paused) return;

      const now = Date.now();
      const elapsed = now - playbackClockRef.current.updatedAt;
      const nextPosition = Math.min(
        currentState.duration,
        playbackClockRef.current.position + elapsed
      );
      const nextState = { ...currentState, position: nextPosition };
      playbackClockRef.current = { position: nextPosition, updatedAt: now };
      playbackStateRef.current = nextState;
      setPlaybackState(nextState);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [playbackState?.paused, playbackState?.track_window.current_track.id]);

  useEffect(() => {
    if (getNativeBridge() || !playbackState || !('mediaSession' in navigator)) return;
    const track = playbackState.track_window.current_track;
    const artwork = track.album.images
      .filter(image => Boolean(image.url))
      .map(image => ({ src: image.url }));
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.name,
      artist: track.artists.map(artist => artist.name).join(', '),
      album: track.album.name,
      artwork,
    });
    navigator.mediaSession.playbackState = playbackState.paused ? 'paused' : 'playing';
    if (playbackState.duration > 0) {
      navigator.mediaSession.setPositionState({
        duration: playbackState.duration / 1000,
        playbackRate: 1,
        position: Math.min(playbackState.position, playbackState.duration) / 1000,
      });
    }
    const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler) => {
      try { navigator.mediaSession.setActionHandler(action, handler); } catch { /* Unsupported on this browser. */ }
    };
    setHandler('play', () => {
      if (getNativeBridge()) postNativeCommand({ action: 'resume' });
      else void playerRef.current?.resume();
    });
    setHandler('pause', () => {
      if (getNativeBridge()) postNativeCommand({ action: 'pause' });
      else void playerRef.current?.pause();
    });
    setHandler('previoustrack', () => { void playerRef.current?.previousTrack(); });
    setHandler('nexttrack', () => { void playerRef.current?.nextTrack(); });
    setHandler('seekto', (details) => {
      if (details.seekTime !== undefined) void seek(details.seekTime * 1000);
    });
  }, [playbackState, seek]);

  const reconnect = useCallback(async () => {
    setError(null);
    setStatus('loading');
    if (getNativeBridge()) {
      postNativeCommand({ action: 'connect' });
      return;
    }
    if (!playerRef.current) {
      initializePlayer();
      return;
    }
    const connected = await playerRef.current.connect();
    if (!connected) {
      setStatus('error');
      setError('Spotify could not reconnect the in-app player.');
    }
  }, [initializePlayer]);

  const value = useMemo<SpotifyPlaybackContextType>(() => ({
    deviceId,
    error,
    playbackState,
    queue,
    queueContext,
    status,
    playAlbum,
    playArtist,
    playTrack,
    playTrackInContext,
    togglePlay,
    nextTrack,
    previousTrack,
    seek,
    refreshQueue,
    reconnect,
  }), [
    deviceId,
    error,
    playbackState,
    queue,
    queueContext,
    status,
    playAlbum,
    playArtist,
    playTrack,
    playTrackInContext,
    togglePlay,
    nextTrack,
    previousTrack,
    seek,
    refreshQueue,
    reconnect,
  ]);

  return (
    <SpotifyPlaybackContext.Provider value={value}>
      {children}
    </SpotifyPlaybackContext.Provider>
  );
};

export const useSpotifyPlayback = (): SpotifyPlaybackContextType => {
  const context = useContext(SpotifyPlaybackContext);
  if (!context) throw new Error('useSpotifyPlayback must be used within SpotifyPlaybackProvider');
  return context;
};
